import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { format, addMonths } from 'date-fns';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon } from 'lucide-react';
import { useFormatting } from '@/hooks/useFormatting';

interface LoseCustomerWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: (eventData: any) => void;
}

export function LoseCustomerWizard({ open, onOpenChange, onComplete }: LoseCustomerWizardProps) {
  const { formatCurrency } = useFormatting();
  const [step, setStep] = useState(1);
  
  // Step 1: Basic Information
  const [customerName, setCustomerName] = useState('');
  const [monthlyRevenueLost, setMonthlyRevenueLost] = useState('50000');
  const [endDate, setEndDate] = useState<Date>(new Date(2026, 2, 1));

  // Step 2: Cost Reductions
  const [reduceStaff, setReduceStaff] = useState(false);
  const [staffReduction, setStaffReduction] = useState('10000');
  const [reduceRent, setReduceRent] = useState(false);
  const [rentReduction, setRentReduction] = useState('2000');
  const [reduceOther, setReduceOther] = useState(false);
  const [otherReduction, setOtherReduction] = useState('1000');
  const [otherDescription, setOtherDescription] = useState('');

  // Step 3: Timing
  const [timing, setTiming] = useState<'immediate' | 'phased'>('immediate');
  const [phaseMonths, setPhaseMonths] = useState('3');

  // Step 4: Review
  const [description, setDescription] = useState('');

  const handleNext = () => setStep(step + 1);
  const handleBack = () => setStep(step - 1);

  const handleComplete = () => {
    const eventData = {
      type: 'lose_customer',
      customerName,
      monthlyRevenueLost: parseFloat(monthlyRevenueLost),
      endDate,
      reduceStaff,
      staffReduction: reduceStaff ? parseFloat(staffReduction) : 0,
      reduceRent,
      rentReduction: reduceRent ? parseFloat(rentReduction) : 0,
      reduceOther,
      otherReduction: reduceOther ? parseFloat(otherReduction) : 0,
      otherDescription,
      timing,
      phaseMonths: timing === 'phased' ? parseInt(phaseMonths) : 0,
      description,
    };
    onComplete(eventData);
    onOpenChange(false);
    resetForm();
  };

  const resetForm = () => {
    setStep(1);
    setCustomerName('');
    setMonthlyRevenueLost('50000');
    setEndDate(new Date(2026, 2, 1));
    setReduceStaff(false);
    setStaffReduction('10000');
    setReduceRent(false);
    setRentReduction('2000');
    setReduceOther(false);
    setOtherReduction('1000');
    setOtherDescription('');
    setTiming('immediate');
    setPhaseMonths('3');
    setDescription('');
  };

  const calculateTotalCostReduction = () => {
    let total = 0;
    if (reduceStaff) total += parseFloat(staffReduction) || 0;
    if (reduceRent) total += parseFloat(rentReduction) || 0;
    if (reduceOther) total += parseFloat(otherReduction) || 0;
    return total;
  };

  const calculate12MonthImpact = () => {
    const revenueLost = parseFloat(monthlyRevenueLost) || 0;
    const costSavings = calculateTotalCostReduction();
    const monthlyNet = costSavings - revenueLost;
    
    const totalRevenueLost = revenueLost * 12;
    const totalCostSavings = costSavings * 12;
    const netImpact = totalCostSavings - totalRevenueLost;
    
    return { totalRevenueLost, totalCostSavings, netImpact, monthlyNet };
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Lose Existing Customer Event - Step {step} of 4</DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4">
            <h3 className="font-semibold">Basic Information</h3>

            <div className="space-y-2">
              <Label htmlFor="customerName">Customer Name *</Label>
              <Input
                id="customerName"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="e.g., OldCo Ltd"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="monthlyRevenueLost">Monthly Revenue Lost *</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm">£</span>
                <Input
                  id="monthlyRevenueLost"
                  type="number"
                  value={monthlyRevenueLost}
                  onChange={(e) => setMonthlyRevenueLost(e.target.value)}
                />
                <span className="text-sm text-muted-foreground">per month</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label>End Date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(endDate, 'PPP')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar mode="single" selected={endDate} onSelect={(date) => date && setEndDate(date)} />
                </PopoverContent>
              </Popover>
              <p className="text-sm text-muted-foreground">When the customer relationship ends</p>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={handleNext} disabled={!customerName || !monthlyRevenueLost}>
                Next: Cost Reductions →
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <h3 className="font-semibold">Cost Reductions</h3>
            <p className="text-sm text-muted-foreground">Select costs you can reduce as a result:</p>

            <div className="space-y-4 border rounded-lg p-4">
              <div className="flex items-start gap-2">
                <Checkbox
                  id="reduceStaff"
                  checked={reduceStaff}
                  onCheckedChange={(checked) => setReduceStaff(checked as boolean)}
                />
                <div className="flex-1 space-y-2">
                  <Label htmlFor="reduceStaff" className="font-semibold">Staff Reduction</Label>
                  {reduceStaff && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm">£</span>
                      <Input
                        type="number"
                        value={staffReduction}
                        onChange={(e) => setStaffReduction(e.target.value)}
                        className="w-32"
                      />
                      <span className="text-sm text-muted-foreground">per month saved</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-4 border rounded-lg p-4">
              <div className="flex items-start gap-2">
                <Checkbox
                  id="reduceRent"
                  checked={reduceRent}
                  onCheckedChange={(checked) => setReduceRent(checked as boolean)}
                />
                <div className="flex-1 space-y-2">
                  <Label htmlFor="reduceRent" className="font-semibold">Rent/Office Space Reduction</Label>
                  {reduceRent && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm">£</span>
                      <Input
                        type="number"
                        value={rentReduction}
                        onChange={(e) => setRentReduction(e.target.value)}
                        className="w-32"
                      />
                      <span className="text-sm text-muted-foreground">per month saved</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-4 border rounded-lg p-4">
              <div className="flex items-start gap-2">
                <Checkbox
                  id="reduceOther"
                  checked={reduceOther}
                  onCheckedChange={(checked) => setReduceOther(checked as boolean)}
                />
                <div className="flex-1 space-y-2">
                  <Label htmlFor="reduceOther" className="font-semibold">Other Cost Reductions</Label>
                  {reduceOther && (
                    <>
                      <Input
                        placeholder="e.g., Software licenses, marketing"
                        value={otherDescription}
                        onChange={(e) => setOtherDescription(e.target.value)}
                      />
                      <div className="flex items-center gap-2">
                        <span className="text-sm">£</span>
                        <Input
                          type="number"
                          value={otherReduction}
                          onChange={(e) => setOtherReduction(e.target.value)}
                          className="w-32"
                        />
                        <span className="text-sm text-muted-foreground">per month saved</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-muted p-4 rounded-lg">
              <p className="text-sm font-semibold">Total Monthly Cost Savings:</p>
              <p className="text-lg text-green-600">{formatCurrency(calculateTotalCostReduction())}/month</p>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={handleBack}>← Back</Button>
              <Button onClick={handleNext}>Next: Timing →</Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <h3 className="font-semibold">Implementation Timing</h3>

            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <input
                  type="radio"
                  id="immediate"
                  checked={timing === 'immediate'}
                  onChange={() => setTiming('immediate')}
                />
                <Label htmlFor="immediate">
                  <span className="font-semibold">Immediate</span>
                  <p className="text-sm text-muted-foreground">Revenue loss and cost savings happen on end date</p>
                </Label>
              </div>

              <div className="flex items-start gap-2">
                <input
                  type="radio"
                  id="phased"
                  checked={timing === 'phased'}
                  onChange={() => setTiming('phased')}
                  className="mt-1"
                />
                <div className="flex-1">
                  <Label htmlFor="phased">
                    <span className="font-semibold">Phased Exit</span>
                    <p className="text-sm text-muted-foreground">Revenue declines gradually over time</p>
                  </Label>
                  {timing === 'phased' && (
                    <div className="mt-2 flex items-center gap-2">
                      <span className="text-sm">Phase out over</span>
                      <Input
                        type="number"
                        value={phaseMonths}
                        onChange={(e) => setPhaseMonths(e.target.value)}
                        className="w-20"
                      />
                      <span className="text-sm">months</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {timing === 'phased' && (
              <div className="bg-muted p-4 rounded-lg space-y-2">
                <p className="font-semibold text-sm">Phased Impact:</p>
                <p className="text-sm">
                  Revenue will decline by {formatCurrency(parseFloat(monthlyRevenueLost) / parseInt(phaseMonths))}/month
                </p>
                <p className="text-sm">
                  Cost savings will be implemented proportionally
                </p>
                <p className="text-sm">
                  Complete exit: {format(addMonths(endDate, parseInt(phaseMonths)), 'MMM yyyy')}
                </p>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={handleBack}>← Back</Button>
              <Button onClick={handleNext}>Next: Review →</Button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4">
            <h3 className="font-semibold">Review Event Summary</h3>

            <div className="space-y-2">
              <p className="font-semibold">Losing Customer: {customerName}</p>
              <p className="text-sm">Monthly Revenue Lost: {formatCurrency(parseFloat(monthlyRevenueLost))}</p>
              <p className="text-sm">End Date: {format(endDate, 'MMM d, yyyy')}</p>
              <p className="text-sm">Timing: {timing === 'immediate' ? 'Immediate' : `Phased over ${phaseMonths} months`}</p>
            </div>

            <div className="border-t pt-4 space-y-3">
              <p className="font-semibold">─── CASH FLOW IMPACT ───────────────────────────</p>

              <p className="font-semibold text-sm">Monthly Revenue Loss ({format(endDate, 'MMM yyyy')} onwards):</p>
              <p className="text-sm text-red-600">• Revenue: -{formatCurrency(parseFloat(monthlyRevenueLost))}/month</p>

              {calculateTotalCostReduction() > 0 && (
                <>
                  <p className="font-semibold text-sm mt-4">Monthly Cost Savings:</p>
                  {reduceStaff && (
                    <p className="text-sm text-green-600">• Staff reduction: +{formatCurrency(parseFloat(staffReduction))}/month</p>
                  )}
                  {reduceRent && (
                    <p className="text-sm text-green-600">• Rent reduction: +{formatCurrency(parseFloat(rentReduction))}/month</p>
                  )}
                  {reduceOther && (
                    <p className="text-sm text-green-600">• {otherDescription || 'Other'}: +{formatCurrency(parseFloat(otherReduction))}/month</p>
                  )}
                  <p className="text-sm">Total savings: +{formatCurrency(calculateTotalCostReduction())}/month</p>
                </>
              )}

              <p className="text-sm font-semibold mt-4">
                Net monthly impact: {calculate12MonthImpact().monthlyNet >= 0 ? '+' : ''}£
                {formatCurrency(calculate12MonthImpact().monthlyNet)}/month
              </p>

              <p className="font-semibold text-sm mt-4">─── NET IMPACT (12 months) ──────────────────────</p>
              <p className="text-sm">Total revenue lost: -{formatCurrency(calculate12MonthImpact().totalRevenueLost)}</p>
              <p className="text-sm">Total cost savings: +{formatCurrency(calculate12MonthImpact().totalCostSavings)}</p>
              <p className="text-sm font-semibold">
                Net 12-month impact: {calculate12MonthImpact().netImpact >= 0 ? '+' : ''}
                {formatCurrency(calculate12MonthImpact().netImpact)}
              </p>

              {calculate12MonthImpact().netImpact < 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mt-4">
                  <p className="text-sm text-yellow-800">
                    ⚠️ Warning: This customer loss will result in a net negative cash impact of £
                    {formatCurrency(Math.abs(calculate12MonthImpact().netImpact))} over 12 months
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-2 pt-4">
              <Label htmlFor="description">Event Description (optional):</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g., Customer moving to competitor"
                rows={2}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={handleBack}>← Back</Button>
              <Button onClick={handleComplete}>Add Event to Scenario</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
